import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { flushSync } from 'react-dom';
import { IS_MOBILE } from 'src/helpers/environment';

const MIN_PINCH_ZOOM = 1;
const MAX_PINCH_ZOOM = 5;

interface UseMobilePinchZoomResult {
  pinchZoom: number;
  setMainEl: (el: HTMLElement | null) => void;
}

export function useMobilePinchZoom({ baseZoom }: { baseZoom: number }): UseMobilePinchZoomResult {
  const [mainEl, setMainEl] = useState<HTMLElement | null>(null);
  const [pinchZoom, setPinchZoom] = useState(1);
  const pinchZoomRef = useRef(1);
  const baseZoomRef = useRef(baseZoom);
  const pinchStartRef = useRef<{
    distance: number;
    scrollContainer: HTMLElement;
    worldX: number;
    worldY: number;
    zoom: number;
  } | null>(null);

  useEffect(() => {
    baseZoomRef.current = baseZoom;
  }, [baseZoom]);

  useEffect(() => {
    if (!IS_MOBILE || !mainEl) return;
    const el = mainEl;

    function getDistance(t1: Touch, t2: Touch) {
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function onTouchStart(e: TouchEvent) {
      if (e.touches.length === 2) {
        const target = e.target instanceof HTMLElement ? e.target : el;
        const scrollContainer = findScrollableAncestor(target, el);
        const rect = scrollContainer.getBoundingClientRect();
        const scale = baseZoomRef.current * pinchZoomRef.current;
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        const worldX = (midX - rect.left) / scale + scrollContainer.scrollLeft;
        const worldY = (midY - rect.top) / scale + scrollContainer.scrollTop;
        pinchStartRef.current = {
          distance: getDistance(e.touches[0], e.touches[1]),
          scrollContainer,
          worldX,
          worldY,
          zoom: pinchZoomRef.current,
        };
      }
    }

    function onTouchMove(e: TouchEvent) {
      if (e.touches.length !== 2 || !pinchStartRef.current) return;
      e.preventDefault();
      const pin = pinchStartRef.current;
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const ratio = currentDistance / pin.distance;
      const next = Math.max(MIN_PINCH_ZOOM, Math.min(MAX_PINCH_ZOOM, pin.zoom * ratio));

      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

      if (next !== pinchZoomRef.current) {
        /**
         * The scroll math below relies on `scrollEl`'s post-render geometry
         * (its CSS box at the NEW pinch scale). Without flushSync, React
         * would batch `setPinchZoom` and `<main>`'s transform wouldn't be
         * committed yet — `getBoundingClientRect` would return the OLD
         * rect, the scrollLeft/scrollTop we set would be against the wrong
         * scale, and the world-anchor point would visibly drift each frame.
         */
        flushSync(() => {
          pinchZoomRef.current = next;
          setPinchZoom(next);
        });
      }

      const scrollEl = pin.scrollContainer;
      const rect = scrollEl.getBoundingClientRect();
      const scale = baseZoomRef.current * next;
      scrollEl.scrollLeft = pin.worldX - (midX - rect.left) / scale;
      scrollEl.scrollTop = pin.worldY - (midY - rect.top) / scale;
    }

    function onTouchEnd(e: TouchEvent) {
      if (e.touches.length < 2) pinchStartRef.current = null;
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [mainEl]);

  const setMainElCallback = useCallback((el: HTMLElement | null) => setMainEl(el), []);
  return { pinchZoom, setMainEl: setMainElCallback };
}

/**
 * Computes the fit-to-screen zoom factor (≤ 1) needed to display a page of
 * width `pageWidthPx` inside the current viewport. Returns 1 on desktop or
 * when the page width is unknown.
 */
export function useMobileFitZoom({ pageWidthPx }: { pageWidthPx: number }): number {
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth
  );

  useEffect(() => {
    if (!IS_MOBILE) return;
    function onResize() {
      setViewportWidth(window.innerWidth);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return useMemo(() => {
    if (!IS_MOBILE || !pageWidthPx || !viewportWidth) return 1;
    return Math.min(1, viewportWidth / pageWidthPx);
  }, [pageWidthPx, viewportWidth]);
}

function findScrollableAncestor(target: HTMLElement, defaultEl: HTMLElement): HTMLElement {
  for (
    let node: HTMLElement | null = target;
    node && node !== defaultEl;
    node = node.parentElement
  ) {
    const style = window.getComputedStyle(node);
    const canScroll = style.overflow === 'auto' || style.overflowY === 'auto';
    if (canScroll) return node;
  }
  return defaultEl;
}
